import React, { useRef, useEffect, useMemo, useState } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useLanguage } from './LanguageProvider';
import { Link } from 'react-router-dom';

// Swiper Imports
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

import ResidencyImg from '../Images/residency_service.png';
import CompanyImg from '../Images/company_service.png';
import RelocationImg from '../Images/relocation_service.png';
import JetImg from '../Images/private_jet_interior_luxury_1778327320105.png';
import PropertyImg from '../Images/luxury_real_estate_villa_spain_1778327334195.png';
import ConciergeImg from '../Images/vip_concierge_service_luxury_watch_keys_1778327352793.png';
import Galaxy from './Galaxy';

gsap.registerPlugin(ScrollTrigger);

export const OurServices = () => {
  const { currentLang, isInitialized } = useLanguage();
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle screen resize to toggle between Swiper and Grid
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 992);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const translations = {
    EN: {
      label: 'Expertise',
      sectionTitle: ['Premium Aviation &', 'Specialized Services'],
      sectionSubtitle: 'FlyVia provides bespoke solutions for private aviation, aerial advertising, and Spain residency, company formation, and relocation.',
      jet: 'Private Jet Charter',
      emptyLeg: 'Empty Leg Flights',
      ads: 'Aerial Advertising',
      paramotor: 'Paramotor Campaigns',
      sightseeing: 'Flight Sightseeing',
      helicopter: 'Helicopter Transport',
      residency: 'Spain Residency',
      formation: 'Corporate Setup',
      relocation: 'Elite Relocation',
      properties: 'Luxury Real Estate',
      concierge: 'VIP Concierge',
      discoverMore: 'Discover More',
      statCta: 'View All Services',
    },
    AR: {
      label: 'خبراتنا',
      sectionTitle: ['الطيران المتميز و', 'الخدمات المتخصصة'],
      sectionSubtitle: 'تقدم FlyVia حلولاً مخصصة للطيران الخاص، الإعلانات الجوية، والإقامة في إسبانيا، وتأسيس الشركات.',
      jet: 'طائرات خاصة',
      emptyLeg: 'رحلات إمبتي ليج',
      ads: 'إعلانات جوية',
      paramotor: 'حملات باراموتور',
      sightseeing: 'جولات طيران',
      helicopter: 'نقل مروحي',
      residency: 'الإقامة في إسبانيا',
      formation: 'تأسيس الشركات',
      relocation: 'الانتقال المتميز',
      properties: 'عقارات فاخرة',
      concierge: 'كونسيرج VIP',
      discoverMore: 'اكتشف المزيد',
      statCta: 'جميع الخدمات',
    }
  };

  const t = (key) => {
    const k = currentLang?.toUpperCase?.() || 'EN';
    return translations[k]?.[key] ?? translations.EN[key] ?? key;
  };

  const cardsData = useMemo(() => [
    // ✈ Aviation & Luxury first
    { id: '01', image: '/Gemini_Generated_Image_4pblo04pblo04pbl.png',        title: t('jet'),        link: '/ourservicepage' },
    { id: '02', image: '/empty-lag.jpeg',  title: t('emptyLeg'),   link: '/ourservicepage' },
    { id: '03', image: '/5.png',        title: t('helicopter'), link: '/ourservicepage' },
    { id: '04', image: '/67bae2fe-43f7-4640-8848-2e038b92b118.png',title: t('ads'),        link: '/ourservicepage' },
    { id: '05', image: '/4460dff2-298b-4bd6-aa84-58ad35651c2b.png',           title: t('sightseeing'),link: '/ourservicepage' },
    // 🌍 Europe & Residency
    { id: '06', image: ConciergeImg,       title: t('concierge'),  link: '/ourservicepage' },
    { id: '07', image: PropertyImg,        title: t('properties'), link: '/ourservicepage' },
    { id: '08', image: ResidencyImg,       title: t('residency'),  link: '/ourservicepage' },
    { id: '09', image: CompanyImg,         title: t('formation'),  link: '/ourservicepage' },
    { id: '10', image: RelocationImg,      title: t('relocation'), link: '/ourservicepage' },
  ], [currentLang]);

  useEffect(() => setIsMounted(true), []);

  useEffect(() => {
    if (!isInitialized || !isMounted) return;
    const ctx = gsap.context(() => {
      gsap.from('.os-header-anim', {
        y: 40, opacity: 0, stagger: 0.2, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: '.os-header', start: 'top 85%' }
      });

      // Only animate cards via GSAP if we are not on mobile (Swiper handles its own entry usually)
      if (!isMobile) {
        cardsRef.current.forEach((card, i) => {
          if (!card) return;
          gsap.from(card, {
            y: 60, opacity: 0, duration: 1, delay: (i % 3) * 0.2,
            scrollTrigger: { trigger: card, start: 'top 90%' }
          });
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, [currentLang, isInitialized, isMounted, cardsData, isMobile]);

  if (!isMounted || !isInitialized) return null;
  const isAR = currentLang === 'ar';

  // Component for the card to avoid repetition
  const ServiceCard = ({ card, index }) => (
    <Link to={card.link} className="os-card-link fv-glow-hover" style={{ textDecoration: 'none', display: 'block', borderRadius: '4px' }}>
      <div className="os-card">
        <div className="os-frame"></div>
        <div className="os-image-wrapper">
          <img src={card.image} alt={card.title} />
          <div className="os-img-overlay" />
        </div>
        <h3 className="os-card-title">{card.title}</h3>
        <span className="os-card-cta">{t('discoverMore')}</span>
      </div>
    </Link>
  );

  return (
    <>
      <style>{`
        .os-section { font-family: var(--font-primary); position: relative; }
        .os-card {
          position: relative;
          padding: 20px 20px 40px;
          text-align: center;
          transition: transform 0.4s ease;
        }
        
        /* Desktop Frame Logic */
        .os-frame {
          position: absolute;
          top: 60px;
          left: 10px;
          right: 10px;
          bottom: 20px;
          border: 1px solid rgba(174, 0, 1, 0.4);
          z-index: 0;
          transition: all 0.4s ease;
        }
        .os-card:hover .os-frame {
          top: 10px;
          border-color: #ae0001;
          box-shadow: 0 0 30px rgba(174, 0, 1, 0.1);
        }

        .os-image-wrapper {
          position: relative;
          z-index: 1;
          width: 100%;
          aspect-ratio: 3/4;
          overflow: hidden;
          margin-bottom: 30px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        .os-image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 1s ease;
        }
        .os-card:hover .os-image-wrapper img { transform: scale(1.1); }
        .os-img-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(174,0,1,0.45) 0%, transparent 60%);
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: 2;
        }
        .os-card:hover .os-img-overlay { opacity: 1; }

        .os-card-title {
          font-family: var(--font-heading);
          font-size: 24px;
          color: #fff;
          margin-top: 20px;
          font-weight: 300;
          letter-spacing: 1px;
          position: relative;
          z-index: 1;
        }
        
        .os-card-cta {
          font-family: var(--font-secondary);
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: #ae0001;
          margin-top: 15px;
          display: block;
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.3s ease;
        }
        .os-card:hover .os-card-cta { opacity: 1; transform: translateY(0); }

        /* Small Screen Specific Styles */
        @media (max-width: 991px) {
          .os-image-wrapper {
            aspect-ratio: 1/1; /* Makes the card height smaller */
            margin-bottom: 15px;
          }
          .os-card {
            padding: 10px 10px 30px;
          }
          .os-card-title {
            font-size: 18px;
            margin-top: 10px;
          }
          .os-frame {
            top: 40px; /* Adjust frame for smaller image */
          }
          .swiper-pagination-bullet {
            background: #ae0001 !important;
          }
          .swiper-pagination {
            bottom: -5px !important;
          }
          .os-card-cta {
            opacity: 1; /* Keep CTA visible on touch devices */
            transform: translateY(0);
          }
        }

        .os-header { margin-bottom: 80px; }
        .os-label { font-family: var(--font-secondary); font-size: 14px; letter-spacing: 0.4em; text-transform: uppercase; color: #ae0001; margin-bottom: 20px; display: block; }
        .os-title { font-family: var(--font-heading); font-size: clamp(32px, 5vw, 56px); color: #fff; font-weight: 300; line-height: 1.1; }
        .os-subtitle { font-family: var(--font-secondary); font-size: 16px; color: rgba(255,255,255,0.5); max-width: 600px; margin: 30px auto 0; line-height: 1.6; }
        .os-watermark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80%;
          max-width: 800px;
          opacity: 0.05;
          z-index: 0;
          pointer-events: none;
          filter: grayscale(100%) contrast(1.2);
        }
      `}</style>

      <section
        ref={sectionRef}
        className="os-section py-32 overflow-hidden"
        style={{
          background: 'var(--premium-bg)',
          paddingTop: '100px',
          paddingBottom: '100px',
        }}
        dir={isAR ? 'rtl' : 'ltr'}
      >
        <img src="/1.png" alt="" className="os-watermark" />
        <div className="container relative z-10">
          <div className="os-header text-center">
            <span className="os-label os-header-anim !text-lg">{t('label')}</span>
            <h2 className="os-title os-header-anim">
              {t('sectionTitle')[0]} <br />
              <span style={{ fontStyle: 'italic', color: '#ae0001' }}>{t('sectionTitle')[1]}</span>
            </h2>
            <p className="os-subtitle os-header-anim">{t('sectionSubtitle')}</p>
          </div>

          {isMobile ? (
            /* Swiper for Mobile — cards already animated by Swiper */
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1.2}
              centeredSlides={true}
              loop={true}
              autoplay={{ delay: 3000 }}
              pagination={{ clickable: true }}
              breakpoints={{
                640: { slidesPerView: 2.2, centeredSlides: false }
              }}
              className="pb-20"
            >
              {cardsData.map((card) => (
                <SwiperSlide key={card.id}>
                  <ServiceCard card={card} />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            /* Standard Grid for Large Screens */
            <div className="row g-4">
              {cardsData.map((card, i) => (
                <div key={card.id} className="col-lg-4 col-md-6" ref={el => cardsRef.current[i] = el}>
                  <ServiceCard card={card} index={i} />
                </div>
              ))}
            </div>
          )}

          <div className="mt-20 text-center">
            <Link to="/ourservicepage" style={{
              fontFamily: 'var(--font-secondary)',
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '0.3em',
              color: 'rgba(255,255,255,0.4)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              paddingBottom: '5px',
              transition: 'all 0.3s ease'
            }}
              onMouseEnter={(e) => e.target.style.color = '#ae0001'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.4)'}
            >
              {t('statCta')}
            </Link>
          </div>
        </div>

        <div
          style={{
            backgroundImage: "url('/145.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.4,
            backgroundAttachment: 'fixed'
          }}
          className="absolute inset-0 w-full h-full pointer-events-none"
        ></div>
      </section>
    </>
  );
};
